import { useLocalization } from "@fluent/react";
import { Button, Checkbox, Row } from "@narmi/design_system";
import * as Sentry from "@sentry/react";
import cc from "classcat";
import { useEffect, useState } from "react";
import { BackButton } from "@lending/components/BackButton/BackButton";
import { Card } from "@lending/components/Card/Card";
import { ErrorScreen } from "@lending/components/ErrorScreen/ErrorScreen";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { PlainButton } from "@lending/components/PlainButton/PlainButton";
import { useAcceptConsumerApplicationDisclosuresMutation } from "@lending/hooks/queries/useAcceptConsumerApplicationDisclosuresMutation";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { useSubmitConsumerApplicationMutation } from "@lending/hooks/queries/useSubmitConsumerApplicationMutation";
import { useNavigateWithSearch } from "@lending/hooks/useNavigateWithSearch";
import { useToastsContext } from "@lending/hooks/useToasts";
import { ensureIovationScripts, getIovationDeviceId } from "@lending/lib/iovation";
import { useFlowNavigation } from "@lending/navigation";
import styles from "./Disclosures.module.css";
import { useStore } from "../../../../contexts/StoreContext";

function MembershipShareDisclosures() {
  const { l10n } = useLocalization();
  const navigate = useNavigateWithSearch();
  const { goBack } = useFlowNavigation();
  const { institution } = useGetInstitutionQuery();
  const { showGenericErrorToast } = useToastsContext();
  const { data } = useStore();
  const acceptDisclosuresMutation = useAcceptConsumerApplicationDisclosuresMutation();
  const submitApplicationMutation = useSubmitConsumerApplicationMutation();
  const daoDisclosures = institution.dao_disclosures ?? [];

  const [acceptedDisclosures, setAcceptedDisclosures] = useState<Record<number, boolean>>({});
  const [errors, setErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    ensureIovationScripts();
  }, []);

  if (daoDisclosures.length === 0) {
    Sentry.captureMessage(
      "No account opening disclosures configured. They are required for the lending required membership share flow.",
      { level: "error" }
    );
    return <ErrorScreen />;
  }

  const handleCheckboxClick = (index: number) => {
    setErrors([]);
    setAcceptedDisclosures((currentAcceptedDisclosures) => ({
      ...currentAcceptedDisclosures,
      [index]: !currentAcceptedDisclosures[index],
    }));
  };

  const handleSubmit = async () => {
    setErrors([]);
    if (!daoDisclosures.every((_, index) => acceptedDisclosures[index])) {
      setErrors(
        daoDisclosures.map((_, index) =>
          acceptedDisclosures[index]
            ? ""
            : l10n.getString("error-must-read-all", null, "Must read and accept all disclosures")
        )
      );
      return;
    }

    if (!data.accountApplicationUuid) {
      Sentry.captureMessage("No account application UUID found when submitting disclosures", {
        level: "error",
      });
      showGenericErrorToast();
      return;
    }

    setIsSubmitting(true);
    try {
      await acceptDisclosuresMutation.mutateAsync({
        params: {
          path: {
            uuid: data.accountApplicationUuid,
          },
        },
      });

      await submitApplicationMutation.mutateAsync({
        params: {
          path: {
            uuid: data.accountApplicationUuid,
          },
        },
        body: {
          device_id: getIovationDeviceId(),
        },
      });

      void navigate("../processing");
    } catch (error) {
      Sentry.captureException(error);
      showGenericErrorToast();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageLayout className={styles.pageLayout}>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.header}>
            <h1 className="margin--bottom--xs">
              {l10n.getString("membership-share-disclosures-heading", null, "Review disclosures")}
            </h1>
            <div className={styles.subheading}>
              {l10n.getString(
                "membership-share-disclosures-subheading",
                null,
                "These documents contain important information for you to review."
              )}
            </div>
          </div>

          <Card>
            <div className={styles.disclosuresList}>
              {daoDisclosures.map((disclosure, index) => (
                <div key={index}>
                  <div
                    className={cc([
                      styles.checkbox,
                      disclosure.links?.some((link) => link.url) && styles.checkboxWithLinks,
                    ])}
                  >
                    <Checkbox
                      name={`accepted-${index}`}
                      label={
                        disclosure?.checkbox_text ??
                        l10n.getString(
                          "disclosures-default-checkbox-text",
                          { institutionShortName: institution.short_name },
                          "I agree to the Terms + Conditions"
                        )
                      }
                      onChange={() => handleCheckboxClick(index)}
                      checked={acceptedDisclosures[index] ?? false}
                      error={errors[index]}
                    />
                  </div>
                  <ul className={styles.disclosureLinks}>
                    {disclosure.links?.map((link, linkIndex) => {
                      if (!link.url) return null;
                      return (
                        <li key={linkIndex} className={styles.disclosureLinkItem}>
                          <PlainButton
                            label={link.text}
                            onClick={() => {
                              window.open(link.url, "_blank", "noopener,noreferrer");
                            }}
                          />
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </Card>

          <div className={styles.buttonContainer}>
            <Row alignItems="center" justifyContent="space-between">
              <BackButton onClick={() => goBack()} />
              <Button
                label={l10n.getString("button-label-submit", null, "Submit")}
                onClick={() => void handleSubmit()}
                disabled={!daoDisclosures.every((_, index) => acceptedDisclosures[index])}
                isLoading={isSubmitting}
              />
            </Row>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}

export { MembershipShareDisclosures };
